import { BLOG_CATEGORIES, BLOG_STATUSES } from '@/constants/blog';
import { ROUTES } from '@/constants/routes';
import { useBlogManagement, useBlogPost } from '@/hooks/use-blog-management';
import type { CreateBlogPostDto } from '@/types/blog';
import { ArrowLeftOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const { TextArea } = Input;
const { Title, Text } = Typography;

const BlogFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [form] = Form.useForm<CreateBlogPostDto>();

  const { handleCreateBlogPost, handleUpdateBlogPost } = useBlogManagement();
  const { data: post, isLoading } = useBlogPost(id);

  const pageTitle = useMemo(() => (isEdit ? 'Edit Blog Post' : 'Create Blog Post'), [isEdit]);

  useEffect(() => {
    if (!isEdit || !post) return;

    form.setFieldsValue({
      slug: post.slug,
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      author: post.author,
      category: post.category,
      read_time: post.read_time,
      image_url: post.image_url,
      resources: post.resources ?? [],
      status: post.status,
      published_at: post.published_at ? dayjs(post.published_at).format('YYYY-MM-DD') : undefined,
      seo_title: post.seo_title ?? undefined,
      seo_description: post.seo_description ?? undefined,
      seo_keywords: post.seo_keywords ?? [],
      og_image_url: post.og_image_url ?? undefined,
    });
  }, [form, isEdit, post]);

  const handleSubmit = async (values: CreateBlogPostDto) => {
    const payload: CreateBlogPostDto = {
      ...values,
      resources: values.resources?.filter((item) => item.title && item.url) ?? [],
      seo_keywords: values.seo_keywords ?? [],
    };

    if (isEdit && id) {
      await handleUpdateBlogPost(id, payload);
    } else {
      await handleCreateBlogPost(payload);
    }

    navigate(ROUTES.BLOG);
  };

  if (isEdit && isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[320px]">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(ROUTES.BLOG)}
          className="px-0"
        >
          Back to blog
        </Button>
        <Title level={3} className="!mb-1">
          {pageTitle}
        </Title>
        <Text type="secondary">Markdown content, SEO fields, and resource links for web and native app.</Text>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          author: 'Mostafa Mohaddes',
          status: 'draft',
          resources: [],
          seo_keywords: [],
        }}
      >
        <Card className="admin-card mb-4" title="Post details">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="title"
                label="Title"
                rules={[{ required: true, message: 'Title is required' }]}
              >
                <Input size="large" placeholder="Post title" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="slug"
                label="Slug"
                rules={[
                  { required: true, message: 'Slug is required' },
                  {
                    pattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                    message: 'Use lowercase letters, numbers, and hyphens only',
                  },
                ]}
              >
                <Input size="large" placeholder="ai-health-monitoring-benefits" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="excerpt"
            label="Excerpt"
            rules={[{ required: true, message: 'Excerpt is required' }]}
          >
            <TextArea rows={3} placeholder="Short summary for listings and SEO" />
          </Form.Item>

          <Form.Item
            name="content"
            label="Content (Markdown)"
            rules={[{ required: true, message: 'Content is required' }]}
          >
            <TextArea rows={16} placeholder="# Heading\n\nWrite markdown content here..." />
          </Form.Item>
        </Card>

        <Card className="admin-card mb-4" title="Metadata">
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="author"
                label="Author"
                rules={[{ required: true, message: 'Author is required' }]}
              >
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: 'Category is required' }]}
              >
                <Select size="large" options={BLOG_CATEGORIES.map((item) => ({ label: item.label, value: item.value }))} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="read_time"
                label="Read time"
                rules={[{ required: true, message: 'Read time is required' }]}
              >
                <Input size="large" placeholder="10 min read" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="image_url"
                label="Hero image URL"
                rules={[
                  { required: true, message: 'Image URL is required' },
                  { type: 'url', message: 'Enter a valid URL' },
                ]}
              >
                <Input size="large" placeholder="https://borzi.app/blog/hero.webp" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                <Select size="large" options={BLOG_STATUSES.map((item) => ({ label: item.label, value: item.value }))} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="published_at" label="Published date">
                <Input size="large" placeholder="2026-02-05" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card className="admin-card mb-4" title="Resources">
          <Form.List name="resources">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Row gutter={12} key={field.key} align="middle" className="mb-3">
                    <Col xs={24} md={10}>
                      <Form.Item
                        {...field}
                        name={[field.name, 'title']}
                        label={field.name === 0 ? 'Title' : undefined}
                        className="mb-0"
                      >
                        <Input placeholder="Resource title" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        {...field}
                        name={[field.name, 'url']}
                        label={field.name === 0 ? 'URL' : undefined}
                        rules={[{ type: 'url', message: 'Enter a valid URL' }]}
                        className="mb-0"
                      >
                        <Input placeholder="https://example.com" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={2} className="flex items-end">
                      <Button
                        type="text"
                        danger
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(field.name)}
                      />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block>
                  Add resource
                </Button>
              </>
            )}
          </Form.List>
        </Card>

        <Card className="admin-card mb-4" title="SEO">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="seo_title" label="SEO title">
                <Input size="large" placeholder="Defaults to post title" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="og_image_url"
                label="OG image URL"
                rules={[{ type: 'url', message: 'Enter a valid URL' }]}
              >
                <Input size="large" placeholder="Defaults to hero image URL" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="seo_description" label="SEO description">
            <TextArea rows={3} placeholder="Defaults to excerpt" />
          </Form.Item>

          <Form.Item name="seo_keywords" label="SEO keywords">
            <Select mode="tags" size="large" placeholder="pet health, cat care, Borzi" />
          </Form.Item>
        </Card>

        <Space>
          <Button type="primary" htmlType="submit" size="large">
            {isEdit ? 'Save changes' : 'Create post'}
          </Button>
          <Button size="large" onClick={() => navigate(ROUTES.BLOG)}>
            Cancel
          </Button>
        </Space>
      </Form>
    </div>
  );
};

export default BlogFormPage;
